/**
 * MembersAndPermissionsSettings
 *
 * Single Settings page with tabs: Members | Permissions (like CategorySettings Expenses | Income).
 */

import { useState, useEffect, type ReactElement } from "react";
import { Box, Tabs, Tab, useTheme, useMediaQuery } from "@mui/material";
import WorkspaceSettings from "./WorkspaceSettings";
import PermissionsSettings, { preloadPermissionsSettings } from "./PermissionsSettings";

interface MembersAndPermissionsSettingsProps {
  workspaceId: string;
}

function MembersAndPermissionsSettings({ workspaceId }: MembersAndPermissionsSettingsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [selectedTab, setSelectedTab] = useState<"members" | "permissions">("members");

  useEffect(() => {
    preloadPermissionsSettings(workspaceId);
  }, [workspaceId]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Tabs
        value={selectedTab === "members" ? 0 : 1}
        onChange={(_, newValue) => setSelectedTab(newValue === 0 ? "members" : "permissions")}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          minHeight: isMobile ? 40 : 48,
          "& .MuiTab-root": { minHeight: isMobile ? 40 : 48, py: isMobile ? 1 : 1.5 },
          "& .MuiTab-root.Mui-selected": { color: "primary.main" },
          "& .MuiTabs-indicator": { backgroundColor: "primary.main" },
        }}
      >
        <Tab label="Members" />
        <Tab label="Permissions" />
      </Tabs>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...(isMobile && { mx: -2, px: 1.5 }),
        }}
      >
        {selectedTab === "members" && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <WorkspaceSettings workspaceId={workspaceId} section="members" fullWidth />
          </Box>
        )}
        {selectedTab === "permissions" && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <PermissionsSettings workspaceId={workspaceId} fullWidth />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default MembersAndPermissionsSettings;
